import { useState, useMemo } from 'react';
import styled from '@emotion/styled';
import { Search, Package, ChevronDown, ChevronUp } from 'lucide-react';
import { Card } from '@template-project/afrodita-components';
import type { Order } from '@/domain/entities/order.entity';
import { TrackOrderCard } from './TrackOrderCard';
import { isOrderActive, isOrderDelivered } from './utils';

const SearchContainer = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.space.md};
  margin-bottom: ${({ theme }) => theme.space.xl};

  @media (max-width: ${({ theme }) => theme.mediaQueries.sm}) {
    flex-direction: column;
  }
`;

const SearchInputWrapper = styled.div`
  position: relative;
  flex: 1;

  svg {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    width: 18px;
    height: 18px;
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

const SearchInput = styled.input`
  width: 100%;
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.md} 40px;
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colors.text.primary};
  }

  &::placeholder {
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`;

const TabBar = styled.div`
  display: flex;
  gap: 0;
  border-bottom: 2px solid ${({ theme }) => theme.colors.border.color};
  margin-bottom: ${({ theme }) => theme.space.xl};
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Tab = styled.button<{ readonly active?: boolean }>`
  padding: ${({ theme }) => theme.space.md} ${({ theme }) => theme.space.lg};
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  white-space: nowrap;
  flex-shrink: 0;
  font-weight: ${({ active, theme }) =>
    active ? theme.typography.config.weight.bold : theme.typography.config.weight.regular};
  color: ${({ active, theme }) =>
    active ? theme.colors.text.primary : theme.colors.text.secondary};
  background: none;
  border: none;
  border-bottom: 2px solid ${({ active }) => (active ? '#2f806a' : 'transparent')};
  margin-bottom: -2px;
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};

  &:hover {
    color: ${({ theme }) => theme.colors.text.primary};
  }
`;

const TabCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: ${({ theme }) => theme.colors.background.secondary};
  font-size: ${({ theme }) => theme.fontSizes[0]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
`;

const EmptyStateCard = styled(Card)`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.space['3xl']} ${({ theme }) => theme.space.xl};
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.background.secondary};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: ${({ theme }) => theme.space.lg};
  color: ${({ theme }) => theme.colors.text.tertiary};
`;

const EmptyText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes[2]}px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0 0 ${({ theme }) => theme.space.sm} 0;
`;

const EmptySubtext = styled.p`
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  color: ${({ theme }) => theme.colors.text.tertiary};
  margin: 0;
`;

const ShowMoreButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.space.sm};
  width: 100%;
  padding: ${({ theme }) => theme.space.md};
  background: ${({ theme }) => theme.colors.background.secondary};
  border: 1px solid ${({ theme }) => theme.colors.border.color};
  border-radius: ${({ theme }) => theme.radius.regular};
  font-size: ${({ theme }) => theme.fontSizes[1]}px;
  font-weight: ${({ theme }) => theme.typography.config.weight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  cursor: pointer;
  font-family: inherit;
  transition: all 0.2s;
  margin-top: ${({ theme }) => theme.space.md};

  &:hover {
    background: ${({ theme }) => theme.colors.background.primary};
    border-color: ${({ theme }) => theme.colors.text.primary};
  }

  svg {
    width: 16px;
    height: 16px;
  }
`;

const LoadingText = styled.p`
  text-align: center;
  padding: ${({ theme }) => theme.space['2xl']};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

const ErrorText = styled.p`
  text-align: center;
  padding: ${({ theme }) => theme.space['2xl']};
  color: #c53030;
`;

type TrackTab = 'in-progress' | 'delivered' | 'all';
const INITIAL_VISIBLE = 3;

function EmptyMessage({ searchQuery, activeTab }: { readonly searchQuery: string; readonly activeTab: TrackTab }) {
  if (searchQuery.trim()) {
    return (
      <>
        <EmptyText>No orders match &ldquo;{searchQuery}&rdquo;</EmptyText>
        <EmptySubtext>Try a different order number or clear the search.</EmptySubtext>
      </>
    );
  }
  if (activeTab === 'in-progress') {
    return (
      <>
        <EmptyText>No orders in progress</EmptyText>
        <EmptySubtext>All your orders have been delivered. Check the &ldquo;Delivered&rdquo; tab.</EmptySubtext>
      </>
    );
  }
  if (activeTab === 'delivered') {
    return (
      <>
        <EmptyText>No delivered orders</EmptyText>
        <EmptySubtext>Your orders haven&apos;t been delivered yet. Check the &ldquo;In Progress&rdquo; tab.</EmptySubtext>
      </>
    );
  }
  return (
    <>
      <EmptyText>No orders found</EmptyText>
      <EmptySubtext>You haven&apos;t placed any orders yet.</EmptySubtext>
    </>
  );
}

interface TrackMyOrderProps {
  readonly orders: Order[];
  readonly isLoading: boolean;
  readonly error: string | null;
  readonly onViewOrderDetails?: (orderId: string) => void;
}

export function TrackMyOrder({
  orders,
  isLoading,
  error,
  onViewOrderDetails,
}: TrackMyOrderProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<TrackTab>('in-progress');
  const [visibleCount, setVisibleCount] = useState(INITIAL_VISIBLE);

  const { inProgress, delivered } = useMemo(() => {
    const ip: Order[] = [];
    const del: Order[] = [];
    for (const order of orders) {
      if (isOrderDelivered(order)) {
        del.push(order);
      } else if (isOrderActive(order)) {
        ip.push(order);
      }
    }
    return { inProgress: ip, delivered: del };
  }, [orders]);

  const filteredOrders = useMemo(() => {
    let source: Order[];
    if (activeTab === 'in-progress') source = inProgress;
    else if (activeTab === 'delivered') source = delivered;
    else source = orders;

    if (!searchQuery.trim()) return source;

    const q = searchQuery.trim().toLowerCase();
    return source.filter(
      (o) =>
        o.number.toLowerCase().includes(q) ||
        o.id.toLowerCase().includes(q)
    );
  }, [activeTab, inProgress, delivered, orders, searchQuery]);

  const visibleOrders = filteredOrders.slice(0, visibleCount);
  const hasMore = filteredOrders.length > visibleCount;

  const handleTabChange = (tab: TrackTab) => {
    setActiveTab(tab);
    setVisibleCount(INITIAL_VISIBLE);
  };

  if (isLoading) {
    return <LoadingText>Loading your orders...</LoadingText>;
  }

  if (error) {
    return <ErrorText>{error}</ErrorText>;
  }

  return (
    <>
      <SearchContainer>
        <SearchInputWrapper>
          <Search size={18} />
          <SearchInput
            type="text"
            placeholder="Search by order number (e.g. SHP-10000010)"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setVisibleCount(INITIAL_VISIBLE);
            }}
          />
        </SearchInputWrapper>
      </SearchContainer>

      <TabBar>
        <Tab
          active={activeTab === 'in-progress'}
          onClick={() => handleTabChange('in-progress')}
        >
          In Progress <TabCount>{inProgress.length}</TabCount>
        </Tab>
        <Tab
          active={activeTab === 'delivered'}
          onClick={() => handleTabChange('delivered')}
        >
          Delivered <TabCount>{delivered.length}</TabCount>
        </Tab>
        <Tab
          active={activeTab === 'all'}
          onClick={() => handleTabChange('all')}
        >
          All Orders <TabCount>{orders.length}</TabCount>
        </Tab>
      </TabBar>

      {filteredOrders.length === 0 ? (
        <EmptyStateCard>
          <EmptyIcon>
            <Package size={28} />
          </EmptyIcon>
          <EmptyMessage searchQuery={searchQuery} activeTab={activeTab} />
        </EmptyStateCard>
      ) : (
        <>
          {visibleOrders.map((order) => (
            <TrackOrderCard
              key={order.id}
              order={order}
              onViewDetails={onViewOrderDetails}
            />
          ))}

          {hasMore && (
            <ShowMoreButton
              onClick={() => setVisibleCount((prev) => prev + INITIAL_VISIBLE)}
            >
              Show more ({filteredOrders.length - visibleCount} remaining)
              <ChevronDown size={16} />
            </ShowMoreButton>
          )}

          {!hasMore && visibleCount > INITIAL_VISIBLE && filteredOrders.length > INITIAL_VISIBLE && (
            <ShowMoreButton onClick={() => setVisibleCount(INITIAL_VISIBLE)}>
              Show less
              <ChevronUp size={16} />
            </ShowMoreButton>
          )}
        </>
      )}
    </>
  );
}
